import { useSettings } from '@wix/tpa-settings/react';
import { useEffect, useMemo, useState, useRef } from 'react';
import type { ItemImage, PopulatedMenu } from 'root/apiTypes';
import { getMenusToDisplay } from './utils';
import type { MenuOrder } from 'root/components/Menus/Settings/Tabs/MenusTab/MenusSettingsParams';
import { settingsParams } from 'root/components/Menus/settingsParams';
import type { MenusDisplayOptions } from 'root/components/Menus/Settings/Tabs/MenusTab/consts';
import { useTranslation } from '@wix/yoshi-flow-editor';
import type { LocationId, LocationsList } from 'root/types/locations';
import { Direction, isSectionNavigationPreset } from 'root/consts';
import { getOrderedLocations, getRemainingLocations, getCurrentLocation } from 'root/utils/location';
import { useGetLayoutStylesParams } from 'root/hooks/useGetLayoutStylesParams';
import { useGetDirection } from 'root/hooks/preset/useGetDirection';
import { useMenusContext } from 'root/contexts/MenusContext';
import { useGetDisplayStylesParams } from 'root/hooks/useGetDisplayStylesParams';
import { useMenuReordering } from 'root/hooks/useMenuReordering';

export const useMenusLogic = ({
  menus = [],
  locations = {},
  initialActiveTab,
  initialLocation,
}: {
  menus: PopulatedMenu[];
  locations?: LocationsList;
  initialActiveTab: number;
  initialLocation?: string;
}) => {
  const { t } = useTranslation();
  const settings = useSettings();
  const { isMobile, isEditor } = useMenusContext();
  const menusSideImage = settings.get(settingsParams.menusSideImage) as ItemImage;
  const { deserializeMenusDisplayOrder } = useMenuReordering();
  const menusDisplayOrder = deserializeMenusDisplayOrder(settings.get(settingsParams.menusDisplayOrder!));
  const menusDisplayOption = settings.get(settingsParams.menusDisplayOption) as MenusDisplayOptions;
  const locationDisplayOrder = settings.get(settingsParams.locationDisplayOrder!) as LocationId[];
  const { showSectionTabs } = useGetDisplayStylesParams();
  const [currentLocation, setCurrentLocation] = useState<LocationId | undefined>(
    initialLocation ??
      getCurrentLocation({
        locationDisplayOrder,
        locations,
      })
  );
  const { preset } = useGetLayoutStylesParams();
  const direction = useGetDirection(preset);

  const displayableLocations = useMemo(() => {
    if (!locations) {
      return {};
    }

    const orderedLocations = getOrderedLocations(
      locationDisplayOrder ?? [],
      locations,
      menus,
      menusDisplayOrder as MenuOrder[]
    );
    const remainingLocations = getRemainingLocations(
      orderedLocations,
      locations,
      menus,
      menusDisplayOrder as MenuOrder[]
    );
    return { ...orderedLocations, ...remainingLocations };
  }, [locations, menus, menusDisplayOrder, locationDisplayOrder]);

  const showLocations = useMemo(() => {
    return Object.keys(displayableLocations).length > 1;
  }, [displayableLocations]);

  const displayableMenus = useMemo(() => {
    let filteredMenus = menus;

    filteredMenus = menus.filter((menu) => !menu.businessLocationDetails?.archived);
    if (showLocations && currentLocation) {
      filteredMenus = menus.filter((menu) => menu.businessLocationId === currentLocation);
    }

    if (!menusDisplayOrder) {
      return filteredMenus;
    }
    return getMenusToDisplay({
      menusDisplayOrder,
      menus: filteredMenus,
      menusDisplayOption,
    });
  }, [menus, showLocations, currentLocation, menusDisplayOrder, menusDisplayOption]);

  const [activeTab, setActiveTab] = useState(initialActiveTab);

  const shouldShowTabs = displayableMenus.length > 1;
  const isRow = shouldShowTabs && showLocations && direction === Direction.Horizontal && !isMobile;

  const prevFirstLocationRef = useRef<string | undefined>(locationDisplayOrder?.[0]);

  useEffect(() => {
    if (isEditor) {
      const firstLocation = locationDisplayOrder?.[0];

      if (firstLocation && firstLocation !== prevFirstLocationRef.current) {
        setCurrentLocation(firstLocation);
        prevFirstLocationRef.current = firstLocation;
      } else if (!firstLocation && Object.keys(displayableLocations).length > 0) {
        const newLocation = Object.keys(displayableLocations)[0];

        if (newLocation !== currentLocation) {
          setCurrentLocation(newLocation);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationDisplayOrder?.[0], displayableLocations]);
  const hasSectionNavigation = showSectionTabs && isSectionNavigationPreset(preset);

  return {
    t,
    menusSideImage,
    activeTab,
    displayableMenus,
    shouldShowTabs,
    menusDisplayOrder,
    setActiveTab,
    displayableLocations,
    showLocations,
    currentLocation,
    setCurrentLocation,
    isRow,
    hasSectionNavigation,
  };
};
